import { Link } from 'react-router-dom';
import { getFooterPaymentIconIds } from '../lib/stripePaymentMethodsData';
import { PaymentBrandIconRow } from './PaymentBrandIcons';
import { useTheme } from './ThemeProvider';

type Props = { className?: string };

const linkGroups: { title: string; links: { to: string; label: string }[] }[] = [
  {
    title: 'Product',
    links: [
      { to: '/scan', label: 'Scan' },
      { to: '/prijzen', label: 'Prijzen' },
      { to: '/betaalmethoden', label: 'Betaalmethoden' },
      { to: '/rapport', label: 'Printrapport' },
      { to: '/account', label: 'Account' },
    ],
  },
  {
    title: 'Ontdek',
    links: [
      { to: '/demo', label: 'Demo' },
      { to: '/faq', label: 'FAQ' },
      { to: '/kennisbank', label: 'Kennisbank' },
      { to: '/over-ons', label: 'Over ons' },
      { to: '/reviews', label: 'Reviews' },
    ],
  },
  {
    title: 'Support',
    links: [
      { to: '/help', label: 'Help' },
      { to: '/troubleshooting', label: 'Troubleshooting' },
      { to: '/contact', label: 'Contact' },
    ],
  },
  {
    title: 'Juridisch',
    links: [
      { to: '/privacy', label: 'Privacy' },
      { to: '/voorwaarden', label: 'Voorwaarden' },
      { to: '/bestelvoorwaarden', label: 'Bestelvoorwaarden' },
      { to: '/disclaimer', label: 'Disclaimer' },
      { to: '/cookies', label: 'Cookies' },
    ],
  },
];

export function SiteFooter({ className = '' }: Props) {
  const { resolvedScheme } = useTheme();
  const footerLogo =
    resolvedScheme === 'light' ? '/branding/logo-footer-light.png' : '/branding/logo-footer-dark.png';

  return (
    <footer
      className={`relative overflow-hidden border-t border-ct-border bg-gradient-to-b from-ct-surface via-ct-surface to-ct-elevated/40 py-14 text-sm text-ct-muted ${className}`}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-ct-accent/35 to-transparent"
        aria-hidden
      />
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-sm text-center sm:text-left">
            <img
              src={footerLogo}
              alt="ComfortTool™"
              className="mx-auto h-10 w-auto object-contain opacity-95 sm:mx-0"
              width={48}
              height={40}
              decoding="async"
            />
            <p className="mt-4 text-balance text-sm leading-relaxed text-ct-muted">
              Indicatieve warmte- en koelcheck voor woning en klein zakelijk. Van scan tot rapport — voor bewoners én
              professionals die snel gedeelde cijfers nodig hebben.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2 sm:justify-start">
              <Link
                to="/scan"
                className="rounded-full bg-ct-accent px-4 py-2 text-xs font-bold text-white transition hover:bg-blue-500"
              >
                Start de scan
              </Link>
              <Link
                to="/inloggen"
                className="rounded-full border border-ct-border bg-ct-surface/80 px-4 py-2 text-xs font-semibold text-ct-text transition hover:border-ct-accent/40"
              >
                Inloggen
              </Link>
            </div>
          </div>
          <div className="grid w-full max-w-2xl grid-cols-2 gap-8 sm:grid-cols-4">
            {linkGroups.map((g) => (
              <div key={g.title}>
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ct-text/80">{g.title}</p>
                <ul className="mt-3 space-y-2.5">
                  {g.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="text-sm text-ct-muted transition hover:text-ct-accent"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 border-t border-ct-border/70 pt-8">
          <p className="text-center text-[11px] font-bold uppercase tracking-[0.16em] text-ct-text sm:text-left">
            Veilig betalen met o.a.
          </p>
          <div className="mt-3 flex justify-center sm:justify-start">
            <PaymentBrandIconRow ids={getFooterPaymentIconIds()} className="justify-center sm:justify-start" />
          </div>
          <p className="mt-3 text-center text-xs text-ct-muted sm:text-left">
            <Link to="/betaalmethoden" className="font-semibold text-ct-accent hover:underline">
              Uitleg per betaalmethode
            </Link>
            {' · '}
            Betaling via Stripe
          </p>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-ct-border/70 pt-8 text-xs sm:flex-row">
          <p className="text-center text-ct-muted/90 sm:text-left">
            © {new Date().getFullYear()} ComfortTool™ ·{' '}
            <a href="https://comforttool.nl" className="text-ct-accent hover:underline">
              comforttool.nl
            </a>
          </p>
          <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2" aria-label="Footer compact">
            <Link to="/" className="text-ct-muted transition hover:text-ct-accent">
              Home
            </Link>
            <Link to="/privacy" className="text-ct-muted transition hover:text-ct-accent">
              Privacy
            </Link>
            <Link to="/disclaimer" className="text-ct-muted transition hover:text-ct-accent">
              Disclaimer
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
